import { Aviso } from '@/components/ui/aviso';
import { Button } from '@/components/ui/button';
import { ToastAction, type ToastActionElement } from '@/components/ui/toast';
import { codigoDeError, mensajeDeError } from '@/lib/api/mensaje-de-error';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { esEmpresaDemo } from './empresa-demo';
import { usePlanCompleto } from './plan-completo-store';
import {
  TEXTOS_DEL_FRENO,
  esCodigoDelFreno,
  textoDelFrenoPorDefecto,
  tituloDelFreno,
  type CodigoDelFreno,
} from './textos-del-estado-de-la-demo';

/**
 * Reglas de la demo (spec 343, filas Productos y Regla): cuando el servidor frena por el tope de
 * productos o por una regla, estrategia o decisión sobre productos propios (403 con `codigo`), la
 * pantalla lo dice con el texto del servidor tal cual y ofrece «Ver el plan completo», que abre el
 * cuadro de toda la app (`usePlanCompleto`, dibujado en el marco). Una sola pieza para todas las
 * vías. El botón no sale en una sesión de soporte («Entrar como»: mira, no actúa en nombre de la
 * persona) ni sin la marca local de demo (el cuadro de toda la app sólo existe en una demo).
 */
export interface FrenoDeLaDemoProps {
  codigo: CodigoDelFreno;
  /** El texto del servidor, tal cual. Sin él, el de siempre de ese código. */
  texto?: string;
  className?: string;
}

/** El freno que trae un error del servidor, o `null` si es otro error. */
export function frenoDelError(err: unknown): FrenoDeLaDemoProps | null {
  const codigo = codigoDeError(err);
  if (!esCodigoDelFreno(codigo)) return null;
  return { codigo, texto: mensajeDeError(err, textoDelFrenoPorDefecto(codigo)) };
}

const puedeVerElPlan = (enSoporte: boolean): boolean => esEmpresaDemo() && !enSoporte;

const abrirElPlan = () => usePlanCompleto.getState().abrir();

/** Para sumar a un aviso (toast) que ya existe: el botón «Ver el plan completo» si es el freno. */
export function accionDelFreno(err: unknown): { action?: ToastActionElement } {
  if (!frenoDelError(err) || !puedeVerElPlan(useAuthStore.getState().isImpersonating)) return {};
  return {
    action: (
      <ToastAction altText={TEXTOS_DEL_FRENO.verElPlan} onClick={abrirElPlan}>
        {TEXTOS_DEL_FRENO.verElPlan}
      </ToastAction>
    ),
  };
}

/**
 * El aviso (toast) del freno, para usar FUERA de un cuadro abierto (dentro, el toast queda debajo
 * del cuadro y su botón no se puede tocar: ahí va `FrenoDeLaDemo`). `null` si el error es otro.
 */
export function avisoDelFreno(err: unknown) {
  const freno = frenoDelError(err);
  if (!freno) return null;
  return { title: tituloDelFreno(freno.codigo), description: freno.texto, ...accionDelFreno(err) };
}

/** El freno dentro de una pantalla o de un cuadro abierto. */
export function FrenoDeLaDemo({ codigo, texto, className }: FrenoDeLaDemoProps) {
  const enSoporte = useAuthStore((s) => s.isImpersonating);
  return (
    <Aviso
      tono="info"
      titulo={tituloDelFreno(codigo)}
      className={className}
      role="status"
      data-testid="freno-de-la-demo"
    >
      <p>{texto ?? textoDelFrenoPorDefecto(codigo)}</p>
      {puedeVerElPlan(enSoporte) && (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="mt-1 h-auto p-0 text-xs"
          onClick={abrirElPlan}
        >
          {TEXTOS_DEL_FRENO.verElPlan}
        </Button>
      )}
    </Aviso>
  );
}
